import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { concatMap, defer, pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  buildSchoolMobileSettingsRequest,
  initialSchoolMobileSettingsState,
  SchoolMobileSettingsPatch,
  SchoolMobileSettingsState,
} from '../entities/school-mobile-settings.model';
import { SchoolMobileSettingsService } from '../infra/school-mobile-settings.service';

const OPS = ['load', 'update'] as const;

/**
 * Ediz app display settings for the authenticated tenant (SchoolService).
 * `load` reads the record (created with the defaults if it never existed) and
 * `update` takes one form's fields, merges them onto the loaded record (id
 * included) and PUTs the whole thing — the response is already the full updated
 * record, so both write into the same `settings` slot and no refetch follows.
 *
 * Refusals (422) carry a localized `Message`; the store forwards the raw error
 * to `ErrorStore.setHttpError`, which surfaces it verbatim.
 */
export const SchoolMobileSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<SchoolMobileSettingsState>(initialSchoolMobileSettingsState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(SchoolMobileSettingsService),
      errorStore = inject(ErrorStore),
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (settings) => patchState(store, { settings }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) => patchState(store, setPropError('load', e)),
              }),
            ),
          ),
        ),
      ),
      update: rxMethod<SchoolMobileSettingsPatch>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          concatMap((changes) =>
            defer(() => service.update(buildSchoolMobileSettingsRequest(store.settings(), changes))).pipe(
              tapResponse({
                next: (settings) => patchState(store, { settings }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              }),
            ),
          ),
        ),
      ),

      reset(): void {
        patchState(store, initialSchoolMobileSettingsState, setPropInit(...OPS));
      },
    }),
  ),
);
